// @flow

import { type GeoPoint, Dimension } from './types';

/** Map a GPSLatitudeRef string to a multiplier (instead of boolean to avoid false/undefined confusion) */
const validLatitudeRefs = new Map([['N', 1], ['S', -1]]);

/** Map a GPSLongitudeRef string to a multiplier (instead of boolean to avoid false/undefined confusion) */
const validLongitudeRefs = new Map([['E', 1], ['W', -1]]);

/**
 * Converts the degrees, minutes and seconds that represent a GPS coordinate in EXIF data to a real number.
 *
 * @returns {*} the coordinate as a real number.
 */
const getGpsCoordinate = ([degrees, minutes, seconds], multiplier: number): number => {
  const decimalValue = degrees + (minutes ? minutes / 60 : 0) + (seconds ? seconds / 3600 : 0);
  return multiplier < 0 ? -decimalValue : decimalValue;
};

/**
 * Gets GPS coordinates from EXIF data in an image.
 *
 * @param mediaUrl      a media URL used to create an image tag from which to read the EXIF data.
 * @param onGetGeoPoint a callback to receive the GPS coordinates if available.
 */
export const getGeoPointFromExif = (mediaUrl: string, onGetGeoPoint: GeoPoint => void) => {
  // Create the HTML image element required by the EXIF library.
  // eslint-disable-next-line no-restricted-globals
  const imgElement = document.createElement('img');

  // Get the EXIF data from the image after it loads (asynchronous).
  // Use old-style callback function that binds 'this' to the image element.
  // eslint-disable-next-line func-names
  imgElement.onload = function() {
    // Get the GPS position from the EXIF data using the callback.
    // Use old-style callback function that binds 'this' to the supplied image ('this').

    import('exif-js').then(EXIF => {
      // eslint-disable-next-line func-names
      EXIF.getData(this, function() {
        // Get coordinates ('this' is the image modified by EXIF).
        const latitudeDegrees = EXIF.getTag(this, 'GPSLatitude');
        const latitudeRef = EXIF.getTag(this, 'GPSLatitudeRef');
        const latitudeMuliplier = validLatitudeRefs.get(latitudeRef);
        const longitudeDegrees = EXIF.getTag(this, 'GPSLongitude');
        const longitudeRef = EXIF.getTag(this, 'GPSLongitudeRef');
        const longitudeMultiplier = validLongitudeRefs.get(longitudeRef);

        // If both available, return the coordinates to the callback.
        if (latitudeDegrees && latitudeMuliplier && longitudeDegrees && longitudeMultiplier) {
          // Transform the coordinate arrays into real numbers and set the geo point in the state.
          const latitudeDecimal = getGpsCoordinate(latitudeDegrees, latitudeMuliplier);
          const longitudeDecimal = getGpsCoordinate(longitudeDegrees, longitudeMultiplier);
          onGetGeoPoint({ latitude: latitudeDecimal, longitude: longitudeDecimal });
        }
      });
    });
  };

  // Complete creating the image with the supplied media URL.
  // eslint-disable-next-line no-restricted-globals
  const srcAttribute = document.createAttribute('src');
  srcAttribute.value = mediaUrl;
  imgElement.setAttributeNode(srcAttribute);
};

/**
 * Get image dimensions.
 *
 * @param mediaUrl        the image url.
 * @param onGetDimensions callback to receive dimensions.
 */
export const getImageDimensions = (mediaUrl: string, onGetDimensions: Dimension => void) => {
  const imgElement = new Image();
  // eslint-disable-next-line func-names
  imgElement.onload = function() {
    onGetDimensions(new Dimension(this.width, this.height));
  };

  imgElement.src = mediaUrl;
};
